import { useCallback, useEffect, useMemo } from 'react'
import { usePersistentState } from '../hooks/usePersistentState'
import trMessages from '../locales/tr.json'
import enMessages from '../locales/en.json'
import {
  I18nContext,
  type I18nContextValue,
  type Locale,
  type TranslationParams,
} from './i18nContextBase'

const dictionaries: Record<Locale, Record<string, unknown>> = {
  tr: trMessages as Record<string, unknown>,
  en: enMessages as Record<string, unknown>,
}

const getNested = (object: Record<string, unknown>, path: string[]): unknown => {
  return path.reduce<unknown>((current, segment) => {
    if (current && typeof current === 'object') {
      return (current as Record<string, unknown>)[segment]
    }
    return undefined
  }, object)
}

const formatMessage = (value: string, params?: TranslationParams) => {
  if (!params) return value
  return value.replace(/\{(\w+)\}/g, (match, key) => {
    const replacement = params[key]
    if (replacement === undefined || replacement === null) {
      return match
    }
    return String(replacement)
  })
}

interface I18nProviderProps {
  children: React.ReactNode
}

export function I18nProvider({ children }: I18nProviderProps) {
  const [locale, setLocaleState] = usePersistentState<Locale>('aiconverterpro:locale', 'tr')

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale
    }
  }, [locale])

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next)
  }, [setLocaleState])

  const translate = useCallback(
    (key: string, fallback?: string, params?: TranslationParams) => {
      const path = key.split('.')
      const dictionary = dictionaries[locale] ?? dictionaries.tr
      const value = getNested(dictionary, path)
      if (typeof value === 'string') {
        return formatMessage(value, params)
      }
      if (fallback) {
        return formatMessage(fallback, params)
      }
      return key
    },
    [locale],
  )

  const contextValue = useMemo<I18nContextValue>(
    () => ({ locale, setLocale, t: translate }),
    [locale, setLocale, translate],
  )

  return <I18nContext.Provider value={contextValue}>{children}</I18nContext.Provider>
}
